import React, { useState, useCallback } from "react";
import { Box, Grid, IconButton, InputBase, Toolbar, Typography } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import { styled, alpha } from "@mui/material/styles";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import debounce from 'lodash.debounce';

import { Colors } from "Utils/colors";
import { CustomEllipticalButton } from "Components/Form";
import { AddIcon } from "Utils/images";
import { isValidString } from "Utils/validators";

const drawerWidth: number = 240;

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(3),
    width: "auto",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",

  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    color: "#ADA7A7",
    border: "1px solid #ccc",
    borderRadius: 5,
  },
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

interface IProps {
  title: string;
  subTitle: string;
  btnName?: string;
  onButtonClick?: () => void;
  onInputChange?: (value: string) => void;
}

export const Header = ({ title, subTitle, btnName, onButtonClick, onInputChange }: IProps) => {

  const [open, setOpen] = useState(true);

  const [inputValue, setInputValue] = useState('');

  const debouncedOnInputChange = useCallback(
    debounce((value) => {
      onInputChange && onInputChange(value);
    }, 400),
    []
  );

  const handleSearch = (value:any) => {
    setInputValue(value);
    debouncedOnInputChange(value);
  };

  return (
    <AppBar
      position="absolute"
      open={open}
      sx={{ bgcolor: Colors.primary, pt: "20px" }}
    >
      <Toolbar
        sx={{
          pr: "24px", // keep right padding when drawer closed
          bgcolor: Colors.white,
          boxShadow: "0px 0px 6px #0000001A",
          border: "1px solid #F1F1F1",
          py: 2,
        }}
      >
        <IconButton
          edge="start"
          color="inherit"
          aria-label="open drawer"
          sx={{
            marginRight: "36px",
            ...(open && { display: "none" }),
          }}
        >
          <MenuIcon />
        </IconButton>

        <Grid container>

          <Grid item sm={12} md={5} lg={4}>

            <Box>
              <Typography
                component="h1"
                variant="h6"
                noWrap
                sx={{ flexGrow: { sm: 0, md: 1 }, color: Colors.black }}
              >
                {title}

                <Typography variant="body2" gutterBottom>
                  {subTitle}
                </Typography>
              </Typography>
            </Box>
          </Grid>

          <Grid item sm={6} md={5} lg={6}>
      <Box sx={{ justifyContent: "flex-start" }}>
        <Search>
          <SearchIconWrapper>
            <SearchIcon sx={{ color: "#ADA7A7" }} />
          </SearchIconWrapper>
          <StyledInputBase
            placeholder="Search…"
            inputProps={{ "aria-label": "search" }}
            value={inputValue || ''}
            onChange={(e) => handleSearch(e.target.value)}
            sx={{ minWidth: { sm: "200px", lg: "400px" } }}
          />
        </Search>
      </Box>
    </Grid>

          <Grid item sm={6} md={2} lg={2}>
            <Box
              sx={{
                display: { md: "flex", justifyContent: "flex-end" },
                fontSize: "20px",
              }}
            >
              <CustomEllipticalButton
                buttonSize="lg"
                buttonHeight="md"
                label={btnName || "New Invoice"}
                onBtnClick={() => onButtonClick ? onButtonClick() : {}}
                uppercase={false}
                variant="contained"
                buttonColor={Colors.secondary}
                labelColor={Colors.white}
                buttonContainerStyle={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                startIcon={<AddIcon color={Colors.white} size={12} />}
              />
              <CustomEllipticalButton
                buttonSize="sm"
                buttonHeight="md"
                label=""
                onBtnClick={() => onButtonClick ? onButtonClick() : {}}
                uppercase={false}
                variant="contained"
                buttonColor={Colors.secondary}
                labelColor={Colors.white}
                buttonContainerStyle={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: "1px solid #fff", margin: 0, padding: 0, minWidth: "20px" }}
                endIcon={<ArrowDropDownIcon sx={{ margin: "0 3px 0 -6px" }} />}
              />
            </Box>
          </Grid>

        </Grid>
      </Toolbar>
    </AppBar>
  );
};
